/**
 * O diálogo da exclusão de arquivo na aba Documentos (E216.F4, GUS-317 item 3): o motivo (da lista)
 * e o fundamento escrito, como na edição do exame. O botão diz o que falta até o pedido ficar
 * pronto; a recusa do servidor (arquivo contido num arquivo único, CCB com certificação aberta) fica
 * no diálogo. Excluído, a lista se relê e a mesa rearma a preparação.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Radio,
  Textarea,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  excluirNaMesa,
  ExclusaoNaMesaError,
  faltaParaExcluir,
  FUNDAMENTO_MAXIMO_DA_EXCLUSAO,
  FUNDAMENTO_MINIMO_DA_EXCLUSAO,
  MOTIVOS_DA_EXCLUSAO,
  type MotivoDaExclusaoNaMesa,
  type RecusaDaExclusao,
} from '@/lib/exclusao_na_mesa';

const t = ptBR.exclusaoNaMesa;
const minimo = String(FUNDAMENTO_MINIMO_DA_EXCLUSAO);

export interface ExcluirDocumentoProps {
  client: AxiosInstance;
  caseId: string;
  documento: { id: string; nome: string };
  onFechar: () => void;
  onExcluido: () => void;
}

export function ExcluirDocumento({
  client,
  caseId,
  documento,
  onFechar,
  onExcluido,
}: ExcluirDocumentoProps): JSX.Element {
  const [motivo, setMotivo] = useState<MotivoDaExclusaoNaMesa | null>(null);
  const [fundamento, setFundamento] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [recusa, setRecusa] = useState<RecusaDaExclusao | null>(null);
  const falta = faltaParaExcluir(motivo, fundamento);

  const excluir = async (): Promise<void> => {
    if (motivo === null || falta !== null) return;
    setSalvando(true);
    setRecusa(null);
    try {
      await excluirNaMesa(client, caseId, documento.id, { motivo, fundamento });
      onExcluido();
      onFechar();
    } catch (err) {
      const motivoDaRecusa = err instanceof ExclusaoNaMesaError ? err.recusa : 'falha';
      // Owner, 03/11 (GT-2026-0056): o arquivo da lista que o servidor já não acha saiu numa
      // tentativa anterior (a que a tela deu por falha): relê a lista em vez de acusar erro.
      if (motivoDaRecusa === 'nao_encontrado') {
        onExcluido();
        onFechar();
        return;
      }
      setRecusa(motivoDaRecusa);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(aberto) => (aberto ? undefined : onFechar())}>
      <DialogContent className="max-w-xl" data-testid="excluir-documento">
        <DialogHeader>
          <DialogTitle>{t.titulo}</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-fg">{documento.nome}</span> · {t.descricao}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-sc-3">
          <fieldset className="space-y-sc-1" data-testid="excluir-motivos">
            <legend className="text-body-sm font-medium text-fg">{t.motivo}</legend>
            {MOTIVOS_DA_EXCLUSAO.map((m) => (
              <Radio
                key={m}
                name="motivo-da-exclusao"
                value={m}
                checked={motivo === m}
                onChange={() => setMotivo(m)}
                wrapperClassName="flex w-full items-start"
                data-testid={`excluir-motivo-${m}`}
              >
                {t.motivos[m]}
              </Radio>
            ))}
          </fieldset>
          <Field
            label={t.fundamento}
            description={t.fundamentoAjuda.replace('{minimo}', minimo)}
            required
          >
            <Textarea
              rows={3}
              value={fundamento}
              maxLength={FUNDAMENTO_MAXIMO_DA_EXCLUSAO}
              onChange={(e) => setFundamento(e.target.value)}
              data-testid="excluir-fundamento"
            />
          </Field>
          {recusa !== null ? (
            <p role="alert" className="text-body-sm text-danger-700" data-testid="excluir-erro">
              {t.porQue[recusa]}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          {falta !== null ? (
            <span className="mr-auto text-caption text-fg-muted" data-testid="excluir-falta">
              {t.falta[falta].replace('{minimo}', minimo)}
            </span>
          ) : null}
          <Button type="button" variant="ghost" onClick={onFechar}>
            {t.cancelar}
          </Button>
          <Button
            type="button"
            disabled={falta !== null || salvando}
            onClick={() => void excluir()}
            data-testid="excluir-confirmar"
          >
            {salvando ? t.excluindo : t.confirmar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
